import React, { useEffect, useMemo, useState } from 'react';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import { formatPeriodLabel } from './statsUtils';

ChartJS.register(ArcElement, Tooltip, Legend);

const PIE_COLORS = [
  '#4f46e5',
  '#0f766e',
  '#2563eb',
  '#b45309',
  '#c2415d',
  '#7c3aed',
  '#0891b2',
  '#65a30d',
  '#ea580c',
  '#64748b',
  '#db2777',
  '#059669'
];

const formatNumber = (value) => Number(value || 0).toLocaleString('bg-BG');
const formatPercent = (value) => `${Number(value || 0).toLocaleString('bg-BG')}%`;

const compactItems = (items, limit) => {
  if (!limit || items.length <= limit) return items;
  const visible = items.slice(0, limit);
  const remainder = items.slice(limit);
  const otherCount = remainder.reduce((sum, item) => sum + item.count, 0);
  const total = items.reduce((sum, item) => sum + item.count, 0);
  return [
    ...visible,
    {
      id: 'other',
      name: 'Други',
      count: otherCount,
      percentage: total ? Math.round((otherCount / total) * 1000) / 10 : 0
    }
  ];
};

function PieStatCard({ title, subtitle, items, limit }) {
  const displayedItems = useMemo(() => compactItems(items, limit), [items, limit]);
  const chartData = useMemo(
    () => ({
      labels: displayedItems.map((item) => item.name),
      datasets: [
        {
          data: displayedItems.map((item) => item.count),
          backgroundColor: displayedItems.map(
            (_item, index) => PIE_COLORS[index % PIE_COLORS.length]
          ),
          borderColor: '#fff',
          borderWidth: 2
        }
      ]
    }),
    [displayedItems]
  );
  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => {
              const item = displayedItems[context.dataIndex];
              return `${item.name}: ${formatNumber(item.count)} · ${formatPercent(
                item.percentage
              )}`;
            }
          }
        }
      }
    }),
    [displayedItems]
  );

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
        {displayedItems.length === 0 ? (
          <Box
            sx={{ alignItems: 'center', display: 'flex', height: 280, justifyContent: 'center' }}>
            <Typography color="text.secondary">Няма данни за периода.</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ height: 250, my: 2.5 }}>
              <Pie data={chartData} options={options} aria-label={title} role="img" />
            </Box>
            <Stack spacing={1.1} sx={{ maxHeight: 250, overflowY: 'auto', pr: 0.5 }}>
              {displayedItems.map((item, index) => (
                <Stack
                  key={item.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1.5}>
                  <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                    <Box
                      sx={{
                        bgcolor: PIE_COLORS[index % PIE_COLORS.length],
                        borderRadius: '50%',
                        flexShrink: 0,
                        height: 10,
                        width: 10
                      }}
                    />
                    <Typography variant="body2" noWrap title={item.name}>
                      {item.name}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" gap={0.75} flexShrink={0}>
                    <Typography variant="caption" color="text.secondary">
                      {formatPercent(item.percentage)}
                    </Typography>
                    <Chip size="small" label={formatNumber(item.count)} />
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function TopProducts({ products }) {
  return (
    <TableContainer component={Card} sx={{ height: '100%' }}>
      <Box sx={{ p: 3, pb: 1 }}>
        <Typography variant="h6">Топ 10 продавани продукта</Typography>
        <Typography variant="body2" color="text.secondary">
          Класация по общ брой продадени артикули за избрания период.
        </Typography>
      </Box>
      {products.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 3 }}>
          Няма данни за периода.
        </Typography>
      ) : (
        <Table size="small" aria-label="Топ 10 продавани продукта">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Продукт</TableCell>
              <TableCell align="right">Поръчки</TableCell>
              <TableCell align="right">Бройки</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product, index) => (
              <TableRow key={product.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Stack
                    component="a"
                    href={`https://gotobed.eu/produkt/${product.slugBg}/${product.productId}`}
                    target="_blank"
                    rel="noreferrer"
                    direction="row"
                    alignItems="center"
                    gap={1.5}
                    sx={{ color: 'inherit', textDecoration: 'none' }}>
                    {product.imageUrl ? (
                      <Box
                        component="img"
                        src={product.imageUrl}
                        alt={product.name}
                        loading="lazy"
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          flexShrink: 0,
                          height: 52,
                          objectFit: 'cover',
                          width: 52
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          alignItems: 'center',
                          bgcolor: 'grey.100',
                          borderRadius: 1.5,
                          color: 'grey.400',
                          display: 'flex',
                          flexShrink: 0,
                          height: 52,
                          justifyContent: 'center',
                          width: 52
                        }}>
                        <Inventory2RoundedIcon fontSize="small" />
                      </Box>
                    )}
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color="text.primary"
                        sx={{
                          display: '-webkit-box',
                          overflow: 'hidden',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2
                        }}>
                        {product.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID {product.productId}
                        {product.code ? ` · ${product.code}` : ''}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell align="right">{formatNumber(product.orderCount)}</TableCell>
                <TableCell align="right">
                  <Chip color="primary" size="small" label={formatNumber(product.count)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
}

function TopSizes({ categories }) {
  const [selectedId, setSelectedId] = useState(categories[0]?.id || '');

  useEffect(() => {
    if (!categories.some((category) => category.id === selectedId)) {
      setSelectedId(categories[0]?.id || '');
    }
  }, [categories, selectedId]);

  const category = categories.find((item) => item.id === selectedId) || categories[0];
  const mainCategories = categories.filter((item) => item.group !== 'kids');
  const kidsCategories = categories.filter((item) => item.group === 'kids');
  const largestCount = Math.max(...(category?.items || []).map((item) => item.count), 1);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
        <Typography variant="h6">Топ 10 размери по категория</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
          Най-често поръчваните размери по брой артикули за избрания период.
        </Typography>
        {categories.length === 0 ? (
          <Typography color="text.secondary">Няма данни за размери през периода.</Typography>
        ) : (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="size-category-label">Категория</InputLabel>
              <Select
                labelId="size-category-label"
                label="Категория"
                value={category?.id || ''}
                onChange={(event) => setSelectedId(event.target.value)}>
                {mainCategories.length > 0 && (
                  <ListSubheader sx={{ color: 'primary.main', fontWeight: 750 }}>
                    Основни
                  </ListSubheader>
                )}
                {mainCategories.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
                {kidsCategories.length > 0 && (
                  <ListSubheader
                    sx={{
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      color: 'primary.main',
                      fontWeight: 750,
                      mt: 0.5,
                      pt: 0.5
                    }}>
                    Детски
                  </ListSubheader>
                )}
                {kidsCategories.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" justifyContent="space-between" gap={2} sx={{ mb: 2.5 }}>
              <Typography variant="body2" color="text.secondary">
                Продадени артикули в категорията
              </Typography>
              <Typography variant="body2" fontWeight={750}>
                {formatNumber(category?.total)}
              </Typography>
            </Stack>
            <Stack spacing={2.25}>
              {(category?.items || []).map((item, index) => (
                <Box key={item.id}>
                  <Stack direction="row" justifyContent="space-between" gap={2} mb={0.75}>
                    <Typography variant="body2" fontWeight={650}>
                      {index + 1}. {item.name}
                    </Typography>
                    <Stack direction="row" gap={1} flexShrink={0}>
                      <Typography variant="caption" color="text.secondary">
                        {formatPercent(item.percentage)}
                      </Typography>
                      <Typography variant="body2" fontWeight={750}>
                        {formatNumber(item.count)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={(item.count / largestCount) * 100}
                    aria-label={`${item.name}: ${item.count}`}
                    sx={{
                      bgcolor: 'grey.100',
                      borderRadius: 99,
                      height: 7,
                      '& .MuiLinearProgress-bar': { borderRadius: 99 }
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function OrderInsights({ data }) {
  const periodLabel = data.range
    ? `${formatPeriodLabel(data.range.from, 'day', true)} – ${formatPeriodLabel(
        data.range.to,
        'day',
        true
      )}`
    : '';
  const cityCount = data.cities.filter((city) => city.id !== 'unassigned').length;
  const cityCountLabel = `${formatNumber(cityCount)} ${cityCount === 1 ? 'град' : 'града'}`;

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={1}
        sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="h5">Анализ на поръчките</Typography>
          <Typography color="text.secondary">
            Разпределенията са по продадени бройки, освен градовете, които са по поръчки.
          </Typography>
        </Box>
        <Stack direction="row" gap={1}>
          <Chip label={`${formatNumber(data.summary.orders)} поръчки`} />
          <Chip color="primary" label={`${formatNumber(data.summary.units)} артикула`} />
          <Chip
            color="secondary"
            label={`${formatNumber(data.itemsPerOrder.average)} средно на поръчка`}
          />
        </Stack>
      </Stack>

      <Alert icon={<CalendarMonthRoundedIcon />} severity="info" sx={{ mb: 2.5 }}>
        Всички графики и класации по-долу са само за избрания период: <strong>{periodLabel}</strong>
      </Alert>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6} lg={4}>
          <PieStatCard
            title="Поръчки по градове"
            subtitle={`Всички населени места (${cityCountLabel})`}
            items={data.cities}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <PieStatCard
            title="Поръчки по ден от седмицата"
            subtitle="Дял и брой поръчки"
            items={data.weekdays}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <PieStatCard
            title="Продажби по вид продукт"
            subtitle="Възглавници, завивки и други продуктови категории"
            items={data.productTypes}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <PieStatCard
            title="Артикули в поръчка"
            subtitle={`Средно ${formatNumber(
              data.itemsPerOrder.average
            )} артикула · най-честите количества са първи`}
            items={data.itemsPerOrder.distribution}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <PieStatCard
            title="Поръчки по начин на доставка"
            subtitle="Спиди, Еконт, доставка до адрес и други методи"
            items={data.deliveryMethods}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <PieStatCard
            title="Продажби по материал"
            subtitle="Дял и брой продадени артикули по материал"
            items={data.materials}
            limit={10}
          />
        </Grid>
        <Grid item xs={12} lg={7}>
          <TopProducts products={data.topProducts.slice(0, 10)} />
        </Grid>
        <Grid item xs={12} lg={5}>
          <TopSizes categories={data.sizesByCategory} />
        </Grid>
      </Grid>
    </>
  );
}
